import { useCallback } from 'react'
import { create } from 'zustand'
import { persist } from 'zustand/middleware'

import { useUserQuery } from 'api/user/queries'
import { localStorageKeys } from 'consts/localStorageKeys'
import { getUuidStorageKey } from 'utils/getUuidStorageKey'

type Panel = 'conversations_menu' | 'references' | 'saved_prompts'

const panelDefaults: Record<Panel, boolean> = {
  conversations_menu: true,
  references: true,
  saved_prompts: true,
}

type PanelsState = {
  panels: Partial<Record<string, boolean>>
  openPanel: (panel: Panel, uuid?: string) => void
  closePanel: (panel: Panel, uuid?: string) => void
  togglePanel: (panel: Panel, uuid?: string) => void
}

const usePanelsStateStore = create<PanelsState>()(
  persist(
    set => ({
      panels: {},

      openPanel: (panel: Panel, uuid?: string) =>
        set(state => ({
          panels: { ...state.panels, [getUuidStorageKey({ storageKey: panel, uuid })]: true },
        })),

      closePanel: (panel: Panel, uuid?: string) =>
        set(state => ({
          panels: { ...state.panels, [getUuidStorageKey({ storageKey: panel, uuid })]: false },
        })),

      togglePanel: (panel: Panel, uuid?: string) =>
        set((state) => {
          const storageKey = getUuidStorageKey({ storageKey: panel, uuid })
          const storedValue = state.panels[storageKey]
          const defaultValue = panelDefaults[panel]
          const currentValue = storedValue ?? defaultValue
          const updatedValue = !currentValue

          return {
            panels: { ...state.panels, [storageKey]: updatedValue },
          }
        }),
    }),
    {
      name: localStorageKeys.panels,
    },
  ),
)

export const useClosePanel = () => {
  const closePanelStore = usePanelsStateStore(state => state.closePanel)
  const { data: uuid } = useUserQuery({ select: ({ user }) => user.uuid })

  return useCallback((panel: Panel) => closePanelStore(panel, uuid),
    [closePanelStore, uuid])
}

export const useOpenPanel = () => {
  const openPanelStore = usePanelsStateStore(state => state.openPanel)
  const { data: uuid } = useUserQuery({ select: ({ user }) => user.uuid })

  return useCallback((panel: Panel) => openPanelStore(panel, uuid),
    [openPanelStore, uuid])
}

export const useTogglePanel = () => {
  const togglePanelStore = usePanelsStateStore(state => state.togglePanel)
  const { data: uuid } = useUserQuery({ select: ({ user }) => user.uuid })

  return useCallback((panel: Panel) => togglePanelStore(panel, uuid),
    [togglePanelStore, uuid])
}

export const useIsPanelOpen = (panel: Panel) => {
  const { data: uuid } = useUserQuery({ select: ({ user }) => user.uuid })
  const storedValue = usePanelsStateStore(state =>
    state.panels[getUuidStorageKey({ storageKey: panel, uuid })],
  )
  const defaultValue = panelDefaults[panel]

  return storedValue ?? defaultValue
}
